import { css } from '@emotion/core';
import styled from '@emotion/styled';
import React from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { getFlatCourseAliases } from 'redux/selectors/course';
import LectureHeaderInput, { LectureHeaderInputProps } from 'shared/components/lecture-header-input';
import NvTooltip from 'shared/components/nv-tooltip';
import WhiteBlackColorButtons, { WhiteBlackColorButtonsProps } from 'shared/components/white-black-color-buttons';
import _ from 'underscore';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { hexToRgbaString, gray2 } from 'styles/global_defaults/colors';
import { getCurrentUser } from 'redux/selectors/users';
import { LecturePageMode } from '..';
import { getLectureHeaderHeight, getLectureHeaderTopMargin as getLectureHeaderTopPadding, LecturePageEditContentParams } from '../lecture-page-content';
import LectureTimeEstimateButton from './lecture-time-estimate-button';

/** Sets the header input to width: 100% on its root element */
const StretchedLectureHeaderInput = styled(LectureHeaderInput)`
  width: 100%;
`;
type LectureContentHeaderParams = {
  isHoverOnHeader: boolean;
} & LecturePageEditContentParams;

/** Contains header-level content for the lecture page content area, including the title &
 * time estimate display */
export const LectureContentHeader = (props: LectureContentHeaderParams) => {
  const params = useLecturePageParams();

  const currentUser = useSelector(getCurrentUser);

  const styles = css`
    /** TODO: Probably need to lock this behind a permission */
    ${params.mode === LecturePageMode.EDIT && props.isHoverOnHeader && css`
      background-color: ${hexToRgbaString(gray2, 0.5)};
    `}

    width: 100%;
    padding-top: ${getLectureHeaderTopPadding(props.lecturePage)};
    min-height: ${getLectureHeaderHeight(props.lecturePage)};

    /* Manually setting this on the header input is required in order to achieve the desired spacing
    on hover w/ the bkg color */
    ${StretchedLectureHeaderInput} .bs4-form-control {
      padding-top: 25px;
      padding-bottom: 25px;
      // TODO: Consider moving this into LectureHeaderInput
      color: ${props.lecturePage.viewOptions?.titleColor ?? 'black'};
      max-width: 800px;
      margin-left: auto;
      margin-right: auto;
    }

    .course-title {
      padding-top: 25px;
      padding-bottom: 25px;
    }

    .foreground-color-picker {
      visibility: hidden;
      position: absolute;
      bottom: -8px;
    }

    // Hide the white/black color buttons until the user mouses over the title text
    .header-text-container:hover {
      .foreground-color-picker {
        visibility: visible;
      }
    }
  `;

  const editable = params.mode === LecturePageMode.EDIT;

  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));

  const onHeaderBlur: LectureHeaderInputProps['onBlur'] = (e) => {
    const title = e.target.value;
    if (title) {
      props.saveLecturePageData({ title });
    }
  };

  const setFontColor: WhiteBlackColorButtonsProps['setColor'] = (val) => {
    props.saveLecturePageData({ viewOptions: {
      ...props.lecturePage.viewOptions,
      titleColor: val,
    } });
  };

  const isViewMode = params.mode === LecturePageMode.VIEW;
  const isLecturePageTranslated = !!currentUser.translationPreferenceLanguage;

  return (
    // Note the mt-auto makes this panel align to the bottom as required when a cover image is applied
    <div css={styles} className='d-flex flex-column align-items-center'>
      <React.Fragment>
        <div className='header-text-container position-relative d-flex justify-content-center w-100'>
          <NvTooltip text={t.LECTURE_PAGES.ADMIN.HEADER.TITLE_POPOVER(courseAliases)} enabled={editable}>
            <div className='w-100'>
              <StretchedLectureHeaderInput
                defaultValue={(isLecturePageTranslated && isViewMode) ? props.lecturePage.translatedTitle : props.lecturePage.title}
                onBlur={onHeaderBlur}
                editable={editable}
                textAreaClassName='course-title-l'
                hoverColor='rgba(58,66,77,.3)'
                height={100}
                required
              />
            </div>
          </NvTooltip>
          { editable
          && (
          <div className='foreground-color-picker'>
            <WhiteBlackColorButtons
              whiteTooltipText={t.INSTITUTIONS.FORM.TITLE_TEXT_WHITE()}
              blackTooltipText={t.INSTITUTIONS.FORM.TITLE_TEXT_BLACK()}
              setColor={(val) => setFontColor(val)}
            />
          </div>
          )}
        </div>
        <div className='pt-3 pb-5'>
          <LectureTimeEstimateButton
            estimate={props.lecturePage.estimation}
            readOnly={!editable}
            isHoverOnHeader={props.isHoverOnHeader}
            {...props}
          />
        </div>
      </React.Fragment>
    </div>
  );
};

export default LectureContentHeader;
